import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import './AllSkinsPage.css';
import PageButton from '../../elements/PageButton/PageButton';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {API_BASE} from '../../../utils/apiConfig';
import {
  ALL_FACTION_SKINS,
  getFactionSkinImagePath,
  isFactionSkinVisible
} from '../../../configs/factions/skins/allFactionSkins';
import {COLORS_CONFIG, PLAYER_COLORS} from '../../../configs/colors';
import {getFactionImagePath} from '../../../utils/factions/getFactionImagePath';
import {getFactionRankImagePath} from '../../../utils/factions/getFactionRankImagePath';
import {getFactionSkinAccentColorKey} from '../../../utils/factions/getFactionSkinAccentColorKey';
import {FactionSkinInventoryItem} from '../InventoryPage/InventoryPage';
import {useSortedFactionConfigs} from "../../../hooks/factions/useSortedFactionConfigs";
import {RANK_NAME_COLORS} from "../../../constants/factions";
import {FACTION_SKIN_DEFAULT_TYPE} from "../../../constants/factions/skins";
import {getCachedOrFetch} from "../../../utils/fetchCache";
import {checkIfRankSkinListed, getRankSkinId, RANK_SKIN_LEAGUES} from "../../../configs/rankSkins";
import {getLeagueConfig} from "../../../configs/leagues";

function resolveFactionColor(colorKey, fallbackColor) {
  if (colorKey === PLAYER_COLORS.BROWN) return '#7b3c00';
  if (colorKey === PLAYER_COLORS.BLUE) return '#157be0';
  return COLORS_CONFIG[colorKey] || fallbackColor;
}

const PAGE_ID = 'AllSkinsPage';

function useFactionSkins(faction, isFactionAvailable, availableSkinIds, selectedSkinId, getSelectionColor) {
  const factionId = faction.id;

  return useMemo(() => {
    const ownedSkinIds = new Set(availableSkinIds || []);
    const nonRankSkins = ALL_FACTION_SKINS.filter(
      (skin) => skin.factionId === factionId && isFactionSkinVisible(skin, ownedSkinIds)
    );
    const availableNonRank = nonRankSkins.filter((skin) => ownedSkinIds.has(skin.id));
    const unavailableNonRank = nonRankSkins.filter((skin) => !ownedSkinIds.has(skin.id));

    const buildSkin = (skin) => {
      const isSelected = skin.id === selectedSkinId;
      const selectionColor = isSelected && skin.type !== FACTION_SKIN_DEFAULT_TYPE
        ? getSelectionColor?.(skin)
        : undefined;
      return {...skin, isSelected, selectionColor};
    };

    const skins = [
      buildSkin({
        id: `${factionId}_default`,
        name: 'Обычный скин',
        type: FACTION_SKIN_DEFAULT_TYPE,
        factionId,
        isAvailable: isFactionAvailable,
        priceNumens: 0,
        imagePath: getFactionImagePath(factionId),
      }),
      ...availableNonRank.map((skin) => buildSkin({
        ...skin,
        type: 'non-rank',
        isAvailable: true,
        imagePath: getFactionSkinImagePath(skin.id),
      })),
      ...unavailableNonRank.map((skin) => buildSkin({
        ...skin,
        type: 'non-rank',
        isAvailable: false,
        imagePath: getFactionSkinImagePath(skin.id),
      })),
      // Рейтинговые образы — от младшей лиги к старшей
      ...RANK_SKIN_LEAGUES.map((rank) => buildSkin({
        id: getRankSkinId(factionId, rank),
        name: getLeagueConfig(rank).name,
        type: 'rank',
        rank,
        factionId,
        isAvailable: ownedSkinIds.has(getRankSkinId(factionId, rank)),
        priceNumens: 0,
        imagePath: getFactionRankImagePath(factionId, rank),
      })),
    ];

    // Образ без нового арта (алмазные и рубиновые нереализованных фракций) —
    // только у того, у кого он уже открыт, как и в разделе «Рейтинговые
    // обличия» коллекции
    return skins.filter((skin) => skin.type !== 'rank'
      || checkIfRankSkinListed(skin.rank, skin.isAvailable, skin.factionId));
  }, [factionId, isFactionAvailable, availableSkinIds, selectedSkinId, getSelectionColor]);
}

function FactionSkinsSection({faction, availableSkinIds, selectedSkinId, lastFactionSoundTimeRef, onFactionClick}) {
  const getSelectionColor = useMemo(() => (skin) => {
    if (skin.rank && RANK_NAME_COLORS[skin.rank]) {
      return RANK_NAME_COLORS[skin.rank];
    }
    const colorKey = getFactionSkinAccentColorKey(skin, faction);
    return resolveFactionColor(colorKey, faction.textColor);
  }, [faction]);

  const skins = useFactionSkins(faction, faction.isAvailable, availableSkinIds, selectedSkinId, getSelectionColor);

  return (
    <div className="all-skins-section">
      <div className="all-skins-section-header">
        <h2 className="all-skins-faction-name" style={{color: faction.textColor}}>
          {faction.name}
        </h2>
      </div>
      <div className="inventory-grid all-skins-grid">
        {skins.map((skin) => (
          <FactionSkinInventoryItem
            key={skin.id}
            skin={skin}
            isAvailable={skin.isAvailable}
            selectionColor={skin.selectionColor}
            lastPlayedByFactionRef={lastFactionSoundTimeRef}
            onClick={() => onFactionClick?.(faction.id, skin.id)}
          />
        ))}
      </div>
    </div>
  );
}

function AllSkinsPage({
  onBack,
  onBackToInventory,
  onFactionClick,
  initialFactions,
  initialAvailableSkinIds,
  // Чужая коллекция: выбранные обличия берём из неё, а не из своего профиля
  initialFactionLoadouts,
  // Ник игрока, чью коллекцию смотрим (в своей коллекции не задан)
  userName,
  // Образы отрядов из чужой коллекции — для страницы фракции
  initialUnitSkinIds,
}) {
  usePageAnalytics('AllSkinsPage');

  const auth = useSelector((state) => state.private.auth);
  const factionLoadouts = initialFactionLoadouts || auth.factionLoadouts || {};
  const [availableSkinIds, setAvailableSkinIds] = useState(initialAvailableSkinIds || []);
  const [loading, setLoading] = useState(!(auth.availableFactions?.length > 0 && initialAvailableSkinIds));
  const [error, setError] = useState(null);
  const lastFactionSoundTimeRef = useRef({});
  const hasFetchedRef = useRef(false);

  const factions = useMemo(
    () => initialFactions || auth.availableFactions || [],
    [initialFactions, auth.availableFactions]
  );

  useEffect(() => {
    if (initialAvailableSkinIds !== undefined) {
      setAvailableSkinIds(initialAvailableSkinIds);
      setLoading(false);
      return;
    }

    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchData = async () => {
      try {
        const skinsData = await getCachedOrFetch('available-skins', () => fetch(`${API_BASE}/available-skins`, {
          method: 'GET',
          credentials: 'include',
        }).then(async (res) => {
          if (!res.ok) throw new Error('Failed to fetch skins');
          return res.json();
        }));

        setAvailableSkinIds(skinsData.skins || []);
      } catch (e) {
        console.error('AllSkins fetch error', e);
        setError('Не удалось загрузить данные');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [initialAvailableSkinIds]);

  const sortedFactions = useSortedFactionConfigs(factions);

  const handleFactionClick = (factionId, selectedSkinId) => {
    onFactionClick?.(factionId, selectedSkinId, factions, availableSkinIds, false, {
      factionLoadouts: initialFactionLoadouts,
      unitSkinIds: initialUnitSkinIds,
    });
  };

  return (
    <>
      <PageButton
        className="all-skins-page-floating-back-button"
        onClick={onBackToInventory}
        pageId={PAGE_ID}
        buttonId="back_to_inventory_floating"
      >
        <img
          className="all-skins-page-floating-back-button_icon"
          src="/img/icons/back_navigation.png"
          alt="К коллекции"
          title="К коллекции"
        />
      </PageButton>

      <h1 className="lobby-page-title all-skins-page-title">
        Все обличия фракций
        {userName && <><br/>{userName}</>}
      </h1>

      {loading && <p className="inventory-status">Загрузка...</p>}
      {error && <p className="inventory-status inventory-status_error">{error}</p>}

      {!loading && !error && (
        <div className="all-skins-sections">
          {sortedFactions.map((faction) => (
            <FactionSkinsSection
              key={faction.id}
              faction={faction}
              availableSkinIds={availableSkinIds}
              selectedSkinId={factionLoadouts[faction.id]?.selectedSkin || `${faction.id}_default`}
              lastFactionSoundTimeRef={lastFactionSoundTimeRef}
              onFactionClick={handleFactionClick}
            />
          ))}
        </div>
      )}

      <div className="main-menu-buttons all-skins-page-buttons">
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId="back">
          Назад
        </PageButton>
      </div>
    </>
  );
}

export default AllSkinsPage;
